import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { config } from '../../config';
import './scss/AIChatbot.scss';
import { marked } from 'marked';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import ExpandContent from '../icons/ExpandContent';
import CollapsContent from '../icons/CollapsContent';
import Close from '../icons/Close';
import MoreHoriz from '../icons/MoreHoriz';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import useAICredits from '../../hooks/useAICredits';
import useBoardPermissions from '../../hooks/useBoardPermissions';
import {
  fetchBoardContext,
  setCurrentBoardId,
  selectEffectiveBoardContext
} from '../../redux/Slices/boardContextSlice';

const AIChatbot = (props) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { board } = useSelector(state => state.board);
  const { selectedBoards, searchQuery } = useSelector(state => state.boardContext);

  // Get values from Redux
  const effectiveBoardContext = useSelector(selectEffectiveBoardContext);
  const boardId = board?.shortId;
  const boardTitle = board?.title;


  // Merge logic
  let boardsContext = [];

  if (effectiveBoardContext.boardIds.length > 0) {
    boardsContext = effectiveBoardContext.boardIds;
  }
  else if (boardId) {
    boardsContext = [boardId];
  }



  // Only show chatbot on board pages
  const isBoardPage = location.pathname.startsWith('/b/');

  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true); // Start minimized
  const [isMaximized, setIsMaximized] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [conversationLoading, setConversationLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('ai-chatbot-panel-width');
    return saved ? parseInt(saved) : 400;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [interimText, setInterimText] = useState('');
  const [likedMessages, setLikedMessages] = useState(new Set());
  const [dislikedMessages, setDislikedMessages] = useState(new Set());
  const [readingMessageId, setReadingMessageId] = useState(null);
  const [copiedMessages, setCopiedMessages] = useState(new Set());

  const { user } = useSelector((state) => state.user);

  // AI Credits hook - use workspace ObjectId for API calls
  const aiCredits = useAICredits(board?.wShortId);

  // Board permissions hook - check if user has read-only access
  const boardPermissions = useBoardPermissions(board, user);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatbotRef = useRef(null);
  const baseTextRef = useRef('');

  const { handlePopoverClick } = useHandlePopoverClick();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Listen for custom event to toggle chatbot from navbar
  useEffect(() => {
    const handleToggleChatbot = () => {
      setIsMinimized(prev => {
        const newState = !prev;
        // Focus on input when opening - faster timing
        if (!newState) {
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
            // Scroll to bottom immediately when opening
            scrollToBottom();
          }, 50);
        }
        return newState;
      });
    };

    window.addEventListener('toggleAIChatbot', handleToggleChatbot);
    return () => {
      window.removeEventListener('toggleAIChatbot', handleToggleChatbot);
    };
  }, []);

  // Emit chatbot state changes to navbar and adjust body layout
  useEffect(() => {
    const stateChangeEvent = new CustomEvent('chatbotStateChanged', {
      detail: { isOpen: !isMinimized }
    });
    window.dispatchEvent(stateChangeEvent);

    // Add/remove body class for layout adjustment
    if (!isMinimized) {
      document.body.classList.add('ai-chatbot-open');
      document.body.style.setProperty('--ai-chatbot-width', `${panelWidth}px`);
    } else {
      document.body.classList.remove('ai-chatbot-open');
      document.body.style.removeProperty('--ai-chatbot-width');
    }
  }, [isMinimized, panelWidth]);

  // Load user's board context on mount
  useEffect(() => {
    dispatch(fetchBoardContext());
  }, [dispatch]);

  // Set current board ID when on board page
  useEffect(() => {
    dispatch(setCurrentBoardId(boardId));
  }, [dispatch, boardId]);

  // Load conversations for selected board (don't auto-select)
  useEffect(() => {
    const loadConversations = async () => {
      if (!boardId) return;

      try {
        const response = await fetch(`${config.API_URI}/v1/conversations?&limit=10`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          const conversationsList = data.conversations || [];
          setConversations(conversationsList);
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
      }
    };

    loadConversations();
  }, [boardId]);

  // Save panel width to localStorage
  useEffect(() => {
    localStorage.setItem('ai-chatbot-panel-width', panelWidth.toString());
  }, [panelWidth]);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('ai-chatbot-sidebar-collapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Load sidebar state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('ai-chatbot-sidebar-collapsed');
    if (saved) {
      setIsSidebarCollapsed(JSON.parse(saved));
    }
  }, []);

  // Auto-resize textarea
  const resizeTextarea = useCallback(() => {
    if (inputRef.current) {
      // Store current scroll position
      const scrollTop = inputRef.current.scrollTop;

      // Reset height to auto to get the correct scrollHeight
      inputRef.current.style.height = 'auto';

      // Calculate new height with constraints
      const scrollHeight = inputRef.current.scrollHeight;
      const minHeight = 40;
      const maxHeight = 120;
      const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

      // Set the new height
      inputRef.current.style.height = newHeight + 'px';

      // Restore scroll position if content exceeds max height
      if (scrollHeight > maxHeight) {
        inputRef.current.scrollTop = scrollTop;
      }
    }
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [inputText, resizeTextarea]);

  // Also resize on input change for immediate feedback
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputText(newValue);
    // Update base text when user manually types (not during recording)
    if (!isRecording) {
      baseTextRef.current = newValue;
    }
    // Trigger resize on next frame to ensure DOM is updated
    requestAnimationFrame(() => {
      resizeTextarea();
    });
  };

  // Voice-to-text functionality using Web Speech API
  const startVoiceRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsRecording(true);
      baseTextRef.current = inputText;
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update input text with base text + interim text for real-time display
      if (interimTranscript) {
        const newText = baseTextRef.current + interimTranscript;
        setInputText(newText);
      }

      // Add final transcript to base text and update input
      if (finalTranscript) {
        const newBaseText = baseTextRef.current + finalTranscript + ' ';
        baseTextRef.current = newBaseText;
        setInputText(newBaseText);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone access and try again.');
      } else {
        alert('Speech recognition error: ' + event.error);
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
      setInterimText(''); // Clear interim text when recording ends
      // Update base text to current input text when recording ends
      baseTextRef.current = inputText;
    };

    setMediaRecorder(recognition);
    recognition.start();
  };

  const stopVoiceRecognition = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
      setInterimText(''); // Clear interim text when stopping
      // Update base text to current input text when stopping
      baseTextRef.current = inputText;
    }
  };

  const handleVoiceInput = () => {
    if (isRecording) {
      stopVoiceRecognition();
    } else {
      startVoiceRecognition();
    }
  };

  // API helper for updating message feedback
  const updateMessageFeedback = async (messageId, feedbackType, value) => {
    if (!conversation?.shortId) return;

    try {
      const response = await fetch(`${config.API_URI}/v1/conversations/${conversation.shortId}/messages/${messageId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          feedbackType,
          value
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update feedback');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating message feedback:', error);
      throw error;
    }
  };

  // Message action handlers
  const handleCopyMessage = async (messageContent, messageId) => {
    try {
      // Remove markdown formatting for cleaner copy
      const cleanText = messageContent
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
        .replace(/\*(.*?)\*/g, '$1') // Remove italic
        .replace(/`(.*?)`/g, '$1') // Remove code
        .replace(/#{1,6}\s/g, '') // Remove headers
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Remove links, keep text

      await navigator.clipboard.writeText(cleanText);

      // Show visual feedback with checkmark
      setCopiedMessages(prev => new Set(prev).add(messageId));

      // Update database
      // await updateMessageFeedback(messageId, 'copied', true);

      // Remove checkmark after 2-3 seconds
      setTimeout(() => {
        setCopiedMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }, 2500);

    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const handleLikeMessage = async (messageId) => {
    // One-time action - if already liked or disliked, do nothing
    if (likedMessages.has(messageId) || dislikedMessages.has(messageId)) return;

    try {
      // Optimistic UI update - add like immediately
      setLikedMessages(prev => new Set(prev).add(messageId));

      // Update database
      await updateMessageFeedback(messageId, 'liked', true);

    } catch (error) {
      // Revert optimistic update on error
      setLikedMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });

      console.error('Failed to update like:', error);
    }
  };

  const handleDislikeMessage = async (messageId) => {
    // One-time action - if already liked or disliked, do nothing
    if (likedMessages.has(messageId) || dislikedMessages.has(messageId)) return;

    try {
      // Optimistic UI update - add dislike immediately
      setDislikedMessages(prev => new Set(prev).add(messageId));

      // Update database
      await updateMessageFeedback(messageId, 'disliked', true);

    } catch (error) {
      // Revert optimistic update on error
      setDislikedMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });

      console.error('Failed to update dislike:', error);
    }
  };

  const handleReadAloud = async (messageContent, messageId) => {
    // Stop any current reading
    if (readingMessageId) {
      window.speechSynthesis.cancel();
      if (readingMessageId === messageId) {
        setReadingMessageId(null);
        return;
      }
    }

    // Check if speech synthesis is supported
    if (!('speechSynthesis' in window)) {
      console.error('Speech synthesis not supported');
      return;
    }

    try {
      // Update database for read aloud tracking
      await updateMessageFeedback(messageId, 'readAloud', true);
    } catch (error) {
      console.error('Failed to track read aloud:', error);
    }

    // Clean the text for speech
    const cleanText = messageContent
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/`(.*?)`/g, '$1') // Remove code
      .replace(/#{1,6}\s/g, '') // Remove headers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
      .replace(/[•]/g, '') // Remove bullet points
      .replace(/\n/g, '. '); // Replace newlines with pauses

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setReadingMessageId(messageId);
    };

    utterance.onend = () => {
      setReadingMessageId(null);
    };

    utterance.onerror = () => {
      setReadingMessageId(null);
      console.error('Speech synthesis error');
    };

    window.speechSynthesis.speak(utterance);
  };


  // Resize functionality for split-screen panel
  const handleResizeStart = useCallback((e) => {
    if (isMinimized) return;
    e.preventDefault();
    setIsResizing(true);
  }, [isMinimized]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;

    const newWidth = window.innerWidth - e.clientX;
    const minWidth = 300;
    const maxWidth = window.innerWidth * 0.8;

    const constrainedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
    setPanelWidth(constrainedWidth);

    // Update CSS variable immediately during resize
    if (!isMinimized) {
      document.body.style.setProperty('--ai-chatbot-width', `${constrainedWidth}px`);
    }
  }, [isResizing, isMinimized]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);


  // Conversation selection handler
  const handleConversationSelect = async (conversationId) => {
    try {
      const response = await fetch(`${config.API_URI}/v1/conversations/${conversationId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConversation(data.conversation);
        setMessages(data.conversation.messages || []);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  // Load existing feedback for messages
  const loadMessageFeedback = async (conversationId, messages) => {
    if (!conversationId || !messages.length) {
      // Clear feedback state if no messages
      setLikedMessages(new Set());
      setDislikedMessages(new Set());
      setCopiedMessages(new Set());
      return;
    }

    try {
      const aiMessages = messages.filter(msg => msg.type === 'ai');

      if (aiMessages.length === 0) {
        // Clear feedback state if no AI messages
        setLikedMessages(new Set());
        setDislikedMessages(new Set());
        setCopiedMessages(new Set());
        return;
      }

      const feedbackPromises = aiMessages.map(async (msg) => {
        try {
          const response = await fetch(`${config.API_URI}/v1/conversations/${conversationId}/messages/${msg.id}/feedback`, {
            method: 'GET',
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            return { messageId: msg.id, feedback: data.feedback };
          }
        } catch (error) {
          console.error(`Failed to load feedback for message ${msg.id}:`, error);
        }
        return null;
      });

      const feedbackResults = await Promise.all(feedbackPromises);

      // Update state with loaded feedback
      const newLikedMessages = new Set();
      const newDislikedMessages = new Set();

      feedbackResults.forEach(result => {
        if (result && result.feedback) {
          if (result.feedback.liked) {
            newLikedMessages.add(result.messageId);
          }
          if (result.feedback.disliked) {
            newDislikedMessages.add(result.messageId);
          }
        }
      });

      setLikedMessages(newLikedMessages);
      setDislikedMessages(newDislikedMessages);
      setCopiedMessages(new Set()); // Always clear copied state on reload

    } catch (error) {
      console.error('Failed to load message feedback:', error);
      // Clear state on error
      setLikedMessages(new Set());
      setDislikedMessages(new Set());
      setCopiedMessages(new Set());
    }
  };

  // Initialize empty chat on mount
  useEffect(() => {
    if (boardId && !conversation) {
      // Start with empty messages - no welcome message
      setMessages([]);
      setConversationLoading(false);
    }
  }, [boardId, conversation]);

  // Clear feedback state when conversation changes
  useEffect(() => {
    setLikedMessages(new Set());
    setDislikedMessages(new Set());
    setCopiedMessages(new Set());
  }, [conversation?.shortId]);

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Auto-open sidebar when maximized
  useEffect(() => {
    if (isMaximized) {
      setIsSidebarOpen(true);
    }
  }, [isMaximized]);

  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle board selection from context popover
  const handleBoardContextSelect = (selectedBoards) => {
    // Board context is now managed by Redux, no need for local state
    // The popover will handle saving to Redux automatically
  };

  // Create new conversation
  const createNewConversation = async () => {
    // Reset to empty chat state like ChatGPT
    setMessages([]);
    setConversation(null);

    //     try {
    //       setIsLoading(true);
    //       const response = await fetch(`${config.API_URI}/v1/conversations`, {
    //         method: 'POST',
    //         credentials: 'include',
    //         headers: {
    //           'Content-Type': 'application/json',
    //         },
    //         body: JSON.stringify({
    //           boardId,
    //           title: 'New Conversation',
    //           messageLimit: 50
    //         })
    //       });

    //       if (response.ok) {
    //         const data = await response.json();
    //         setConversation(data.conversation);

    //         // Reset messages with welcome message
    //         setMessages([{
    //           id: 1,
    //           type: 'ai',
    //           content: `Hi! I'm your AI assistant. This is a new conversation with ${data.conversation.messageLimit} messages available.

    // What would you like to know about your board?`,
    //           timestamp: new Date(),
    //           actions: [
    //             { label: 'Get Board Insights', action: 'insights' },
    //             { label: 'Suggest Cards', action: 'suggestions' },
    //             { label: 'Create Task', action: 'create-single-task' }
    //           ]
    //         }]);
    //       } else {
    //         console.error('Failed to create new conversation');
    //       }
    //     } catch (error) {
    //       console.error('Error creating new conversation:', error);
    //     } finally {
    //       setIsLoading(false);
    //     }

  };

  const handleSendMessage = async (text = inputText, actionType = null) => {
    if (!text.trim() && !actionType) return;

    // Check if user has read-only access
    if (boardPermissions.isReadOnly) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `⚠️ **Access Denied!** You're viewing this board in read-only mode. AI actions are restricted to board members only. Please request access to use AI features.`,
        timestamp: new Date(),
        actions: []
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    // Check AI credits using new hybrid system
    if (!aiCredits.canConsumeCredits(1)) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `⚠️ **Insufficient AI credits!** ${aiCredits.getWarningMessage()}. Please upgrade your workspace plan or contact support for more personal credits.`,
        timestamp: new Date(),
        actions: []
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: text || actionType,
      timestamp: new Date()
    };

    // Add user message to messages
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    baseTextRef.current = ''; // Clear base text when sending message
    setIsLoading(true);

    try {
      let endpoint = '';
      let requestBody = {};

      // Natural language query with conversation support
      endpoint = `/v1/ai/nlp/query`;
      requestBody = {
        query: text,
        boardIds: boardsContext, // Array: single, multiple, or empty
        conversationId: conversation?.shortId
      };

      const response = await fetch(`${config.API_URI}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to process request');
      }

      const data = await response.json();

      // ... your existing logic for building aiResponse, actions based on actionType ...

      // Example fallback for AI response (keep your existing code here)
      let aiResponse = data.response || "I'm not sure how to help with that. Try asking about your board performance, requesting card suggestions, or describing tasks you'd like to create.";
      let actions = [];

      // Use the actual message ID returned from the backend if available
      const aiMessage = {
        id: data.aiMessage?.id || Date.now() + 1,
        type: 'ai',
        content: aiResponse,
        timestamp: data.aiMessage?.timestamp ? new Date(data.aiMessage.timestamp) : new Date(),
        actions: actions
      };

      // Update the user message with backend ID if provided
      if (data.userMessage?.id) {
        setMessages(prev => {
          const updatedMessages = [...prev];
          const userMsgIndex = updatedMessages.findIndex(msg => msg.type === 'user' && msg.content === (text || actionType));
          if (userMsgIndex !== -1) {
            updatedMessages[userMsgIndex] = {
              ...updatedMessages[userMsgIndex],
              id: data.userMessage.id,
              timestamp: data.userMessage.timestamp ? new Date(data.userMessage.timestamp) : updatedMessages[userMsgIndex].timestamp
            };
          }
          return [...updatedMessages, aiMessage];
        });
      } else {
        setMessages(prev => [...prev, aiMessage]);
      }

      // Update conversation state if we have conversation info from response
      if (data.conversation) {
        setConversation(prev => {
          if (!prev) {
            return {
              shortId: data.conversation.id,
              title: 'New Conversation',
              board: boardId,
              boardTitle: boardTitle,
              messageCount: data.conversation.messageCount,
              status: data.conversation.status
            };
          }
          return {
            ...prev,
            shortId: data.conversation.id || prev.shortId,
            messageCount: data.conversation.messageCount,
            status: data.conversation.status
          };
        });

        // Reload conversations list if no previous conversation
        if (!conversation) {
          const loadConversations = async () => {
            try {
              const response = await fetch(`${config.API_URI}/v1/conversations?&limit=10`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                }
              });
              if (response.ok) {
                const data = await response.json();
                setConversations(data.conversations || []);
              }
            } catch (error) {
              console.error('Error reloading conversations:', error);
            }
          };
          loadConversations();
        }
      }

      // Update AI credits if provided (new system)
      if (data.credits) {
        aiCredits.updateCreditsFromResponse(data);
      }

    } catch (error) {
      console.error('Chatbot error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "Sorry, I encountered an error while processing your request. Please try again.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };


  const handleActionClick = (action, data = null) => {
    if (action === 'confirm-tasks' && data) {
      // Handle task creation confirmation
      handleSendMessage(`Create ${data.length} tasks`, 'confirm-create-tasks');
    } else if (action === 'view-task' && data?.permalink) {
      // Navigate to the task
      window.open(data.permalink, '_blank');
    } else if (action === 'create-single-task' || action === 'create-another-task') {
      // Prompt for task creation
      const taskPrompt = prompt('Describe the task you want to create:');
      if (taskPrompt) {
        handleSendMessage(taskPrompt); // Let smart AI agent handle it automatically
      }
    } else {
      handleSendMessage('', action);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessageContent = (content) => {
    // Simple markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  };

  // Don't render if not on a board page or no board data
  if (!isBoardPage || !boardId || !boardTitle) {
    return null;
  }

  // Don't render anything when minimized - the navbar intelligence button will open it
  if (isMinimized) {
    return null;
  }


  return (
    <div
      className={`ai-chatbot split-screen ${isMaximized ? 'maximized' : ''} ${isResizing ? 'resizing' : ''}`}
      ref={chatbotRef}
      style={{
        width: `${panelWidth}px`,
        right: 0,
        top: 0,
        bottom: 0
      }}
    >
      {/* Resize Handle */}
      <div
        className="resize-handle"
        onMouseDown={handleResizeStart}
      />

      {/* Sidebar */}
      {isSidebarOpen && (
        <>
          <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
          <div className="chatbot-sidebar">
            <div className="sidebar-header">
              <div className="sidebar-logo">
                <span className="material-symbols-outlined">smart_toy</span>
              </div>
              <button
                className="sidebar-close"
                onClick={() => setIsSidebarOpen(false)}
              >
                <Close size={20} />
              </button>

              {/* <div className="sidebar-actions">



                <button
                  className="sidebar-item"
                  onClick={() => { createNewConversation(); setIsSidebarOpen(false); }}
                >
                  <span className="material-symbols-outlined">edit</span>
                  <span>New chat</span>
                </button>

              </div> */}


            </div>

            <div className="sidebar-content">
              <button className="sidebar-item" onClick={() => { createNewConversation(); setIsSidebarOpen(false); }}>
                <span className="material-symbols-outlined">edit</span>
                <span>New chat</span>
              </button>

              <button className="sidebar-item">
                <span className="material-symbols-outlined">search</span>
                <span>Search chats</span>
              </button>

              <div className="sidebar-section">
                <h4>Chats</h4>
                {filteredConversations?.map((conv) => {
                  return (
                    <button
                      key={conv._id}
                      className="sidebar-item chat-item"
                      onClick={() => { handleConversationSelect(conv._id); setIsSidebarOpen(false); }}
                    >
                      <span>{conv.title}</span>
                      <span className="material-symbols-outlined">more_horiz</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Chat Area */}
      <div className="chatbot-main">
        <div className="chatbot-header" >
          <div className='header-menu'>
            <div
              onClick={toggleSidebar}
              className="action-button"
              title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
              disabled={conversationLoading}
            >
              {!isSidebarOpen && <MoreHoriz />}
            </div>
          </div>
          <div className="header-content">
            <div className="header-text">
              <h3>Workspace Agent</h3>
            </div>
          </div>
          <div className="header-actions">
            <button
              onClick={() => setIsMaximized(!isMaximized)}
              className="action-button"
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {!isMaximized ? <ExpandContent /> : <CollapsContent />}
            </button>

            <button
              onClick={() => setIsMinimized(true)}
              className="action-button chat-close-button"
              title="Close"
            >
              <Close />
            </button>
          </div>
        </div>

        <div className="chatbot-messages">
          {/* ChatGPT-style empty state */}
          {messages.length === 0 && (
            <div className="empty-chat-state">
              <div className="empty-chat-content">
                <h2>How can I help, {user?.user?.name?.split(' ')[0] || 'there'}?</h2>
              </div>
            </div>
          )}

          {messages.map((message) => {
            // Determine message type for styling
            const messageType = message.type;
            const isSystemMessage = message.content.includes('[Voice message recorded]') || message.content.includes('[Board:');
            const isErrorMessage = message.content.includes('❌') || message.content.includes('Sorry, I encountered an error');
            const isSuccessMessage = message.content.includes('✅') || message.content.includes('Task created successfully');

            return (
              <div key={message.id} className={`message ${messageType} ${isSystemMessage ? 'system' : ''} ${isErrorMessage ? 'error' : ''} ${isSuccessMessage ? 'success' : ''}`}>
                {messageType !== 'ai' && (
                  <div className="message-avatar">

                    {user?.user?.profilePicture ? (
                      <img src={user?.user?.profilePicture} alt={user?.user?.name} loading="lazy" />
                    ) : (
                      <ImagePlaceholder key={user?.user?.id} size={40} text={user?.user?.username} onlineIndicator={false} />

                    )}
                  </div>
                )}
                <div className="message-content">
                  <div className="message-header">
                    <span className="message-sender">
                      {messageType === 'ai' ? 'AI Assistant' : 'You'}
                    </span>
                    <span className="message-time">
                      {message?.timestamp && (
                        new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      )}
                    </span>
                  </div>

                  <div
                    className={`message-text ${isSystemMessage ? 'system-text' : ''}`}
                    dangerouslySetInnerHTML={{ __html: marked(message.content) }}
                  />



                  {message.actions && message.actions.length > 0 && !boardPermissions.isReadOnly && (
                    <div className="message-actions">
                      {message.actions.map((action, index) => (
                        <button
                          key={index}
                          onClick={() => handleActionClick(action.action, action.data)}
                          className="action-chip"
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* AI Message Actions */}
                {messageType === 'ai' && (
                  <div className="message-response-actions">
                    <button
                      className={`response-action-btn ${copiedMessages.has(message.id) ? 'copied' : ''}`}
                      onClick={() => handleCopyMessage(message.content, message.id)}
                      data-tooltip-content={copiedMessages.has(message.id) ? "Copied!" : "Copy message"}
                      data-tooltip-position="bottom"
                    >
                      <span className="material-symbols-outlined">
                        {copiedMessages.has(message.id) ? 'check' : 'content_copy'}
                      </span>
                    </button>

                    {/* Show like button only if not disliked */}
                    {!dislikedMessages.has(message.id) && (
                      <button
                        className={`response-action-btn ${likedMessages.has(message.id) ? 'active liked' : ''}`}
                        onClick={() => handleLikeMessage(message.id)}
                        data-tooltip-content={likedMessages.has(message.id) ? "You liked this!" : "Like response"}
                        data-tooltip-position="bottom"
                        disabled={likedMessages.has(message.id)}
                      >
                        <span className="material-symbols-outlined">
                          thumb_up
                        </span>
                      </button>
                    )}

                    {/* Show dislike button only if not liked */}
                    {!likedMessages.has(message.id) && (
                      <button
                        className={`response-action-btn ${dislikedMessages.has(message.id) ? 'active disliked' : ''}`}
                        onClick={() => handleDislikeMessage(message.id)}
                        data-tooltip-content="Dislike response"
                        data-tooltip-position="bottom"
                      >
                        <span className="material-symbols-outlined">
                          thumb_down
                        </span>
                      </button>
                    )}

                    <button
                      className={`response-action-btn ${readingMessageId === message.id ? 'active reading' : ''}`}
                      onClick={() => handleReadAloud(message.content, message.id)}
                      data-tooltip-content={readingMessageId === message.id ? "Stop reading" : "Read aloud"}
                      data-tooltip-position="bottom"
                    >
                      <span className="material-symbols-outlined">
                        {readingMessageId === message.id ? 'stop' : 'volume_up'}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {isLoading && (
            <div className="message ai">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-input">
          <div className="input-container">
            <div className="input-wrapper">
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={
                  boardPermissions.isReadOnly
                    ? "AI features are restricted to board members only."
                    : boardsContext.length > 0
                      ? effectiveBoardContext.type === 'current_board'
                        ? "Ask anything about your board…"
                        : effectiveBoardContext.type === 'selected_boards'
                          ? `Ask about ${effectiveBoardContext.boardIds.length} selected board${boardsContext.length > 1 ? 's' : ''}…`
                          : `Ask about your ${boardsContext.length} boards…`
                      : "Select a board to start chatting"
                }
                className="message-input"
                rows={1}
                disabled={boardPermissions.isReadOnly}
              />
              {interimText && (
                <div className="interim-text-overlay">
                  <span className="interim-text">{interimText}</span>
                </div>
              )}
            </div>
            <div className='bottom-tools'>
              <div className="input-tools">
                <button
                  id='popover-add-board-context'
                  className="tool-button"
                  data-tooltip-content="Add board context"
                  data-tooltip-position="top"
                  data-popover-trigger
                  onClick={(e) => handlePopoverClick(e, 'boardContenxtList', {
                    boardId,
                    onBoardSelect: handleBoardContextSelect
                  })}
                  disabled={!boardId}
                >
                  <span className="material-symbols-outlined">add</span>
                  {selectedBoards.length > 0 && (
                    <span className="board-count">({selectedBoards.length})</span>
                  )}
                </button>

                {/* AI Credits Status */}
                {aiCredits.credits && (
                  <div className="ai-credits-status">
                    {/* Show workspace credits for Pro/Premium workspaces, personal for free */}
                    {aiCredits.credits.workspace && aiCredits.credits.workspace.planType !== 'Free' ? (
                      <div className="credit-display workspace-credits" data-tooltip-content="Workspace AI requests · Monthly renewal" data-tooltip-position="top">
                        <div className="credit-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </div>
                        <span className={`credit-count ${aiCredits.isWorkspaceUsageHigh() ? 'warning' : ''}`}>
                          {aiCredits.credits.workspace.credits}
                        </span>
                        <span className="credit-total">/{aiCredits.credits.workspace.maxCredits}</span>
                      </div>
                    ) : (
                      <div className="credit-display personal-credits" data-tooltip-content="Your AI requests · Signup bonus" data-tooltip-position="top">
                        <div className="credit-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                        <span className={`credit-count ${aiCredits.isPersonalCreditsLow() ? 'warning' : ''}`}>
                          {aiCredits.credits.personal.credits}
                        </span>
                        <span className="credit-total">/{aiCredits.credits.personal.maxCredits}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="input-actions">
                <button
                  className={`tool-button ${isRecording ? 'recording' : ''}`}
                  data-tooltip-content={boardPermissions.isReadOnly ? "Read-only mode" : isRecording ? "Stop recording" : "Voice input"}
                  data-tooltip-position="top"
                  onClick={handleVoiceInput}
                  disabled={boardPermissions.isReadOnly}
                >
                  <span className="material-symbols-outlined">
                    {isRecording ? 'stop' : 'mic'}
                  </span>
                </button>
                <button
                  onClick={() => handleSendMessage()}
                  disabled={boardPermissions.isReadOnly || isLoading || !inputText.trim() || boardsContext.length === 0}
                  className="send-button"
                >
                  <span className="material-symbols-outlined">
                    arrow_upward
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatbot;
